import type { Deallocation } from '@morpho-org/morpho-sdk'
import type { Address } from 'viem'

import {
  type AccrualVaultV2,
  type AccrualVaultV2MorphoMarketV1Adapter,
  type AccrualVaultV2MorphoMarketV1AdapterV2,
  type AccrualVaultV2MorphoVaultV1Adapter,
  type IAccrualVaultV2Adapter,
  type MarketId,
  MarketParams,
  MathLib
} from '@morpho-org/blue-sdk'

/** Throws with a unique invariant code; callers are expected to report to Sentry. */
export function failInvariant(code: string): never {
  throw new Error(code)
}

export interface ForceDeallocateAction {
  adapter: Address
  /** Capacity available from this adapter+market (assets). */
  amount: bigint
  marketParams: MarketParams
  /** Per-adapter force-deallocate penalty (WAD). */
  penalty: bigint
}

export interface MaxForceDeallocateResult {
  totalDeallocatable: bigint
  /** Max shortfall coverable on-chain: `Σ (Dᵢ + ceil(Dᵢ × pᵢ / WAD))`. */
  totalCoverage: bigint
  readonly actions: readonly ForceDeallocateAction[]
}

export const EMPTY_RESULT: MaxForceDeallocateResult = {
  totalDeallocatable: 0n,
  totalCoverage: 0n,
  actions: []
}

/** Hard cap enforced by VaultV2 on every adapter's penalty. */
export const MAX_FORCE_DEALLOCATE_PENALTY = 2n * 10n ** 16n // 2% WAD

/**
 * Slack added to the deallocations backing a full force exit, in basis points. Covers share-price
 * drift between the block the vault snapshot is pinned to and the block the transaction lands in.
 * At the observed ~1.6e-9/s drift this covers days of signing delay.
 *
 * Lives here rather than beside the hook that applies it so the e2e suite can
 * assert its fixture against the real value: importing that hook from a Playwright spec drags in
 * `Web3Context` and `@repo/web3`, which the runner cannot type-strip out of `node_modules`.
 *
 * Not to be confused with `DRIFT_RESERVE_DIVISOR` below, which answers the same hazard on the
 * in-kind path. They differ because the two exits are denominated differently, not because one
 * of them is mis-sized. Here the exit ends in `redeem(shares)`, so the buffer over-deallocates
 * to keep that redeem funded at the execution price, and surplus deallocation is harmless: it
 * stays in vault idle. In-kind exits cannot redeem — `VaultExitBundlesV1` takes an asset amount
 * by construction — so there the reserve is withheld from the exit itself, which strands it as
 * dust in the user's position. Withholding is the more expensive instrument, hence the far
 * smaller coefficient. Neither number is a bound on the other.
 */
export const FORCE_REDEEM_BUFFER_BPS = 10n

// ─── On-chain coverage primitives ────────────────────────────────────────────
//
// VaultV2.forceDeallocate(adapter, D, ...) burns `ceil(D × p / WAD)` assets worth of penalty
// shares and moves `D` assets from the adapter into the vault idle. The combined `D + penalty`
// is what the user "spends" in vault value to free up `D + penalty` of withdrawable balance:
// `D` lands in idle (and is then withdrawn), `penalty` is retained by the vault as fee.
//
// Plans must satisfy the on-chain coverage equation exactly using these primitives.

/** Penalty (assets) charged when deallocating `D` from an adapter with rate `penalty` (WAD). */
export function penaltyAssets(D: bigint, penalty: bigint): bigint {
  return penalty > 0n ? MathLib.mulDivUp(D, penalty, MathLib.WAD) : 0n
}

/** Coverage from one deallocation: `D + ceil(D × p / WAD)`. Strictly non-decreasing in `D`. */
export function adapterCoverage(D: bigint, penalty: bigint): bigint {
  return D + penaltyAssets(D, penalty)
}

/**
 * Smallest `D ∈ [0, cap]` such that `adapterCoverage(D, penalty) ≥ target`. Returns `null` if no
 * `D ≤ cap` reaches `target`.
 *
 * `ceil(target × WAD / (WAD + p))` is the natural upper bound, but it can overshoot by 1–2 wei
 * because both `D` and `penalty(D)` round up. Since coverage steps by 1 or 2 wei per unit of `D`,
 * the tight `D` is found by decrementing at most twice from the upper bound.
 */
export function minDeallocationFor(target: bigint, penalty: bigint, cap: bigint): bigint | null {
  if (target <= 0n) return 0n
  if (cap <= 0n) return null

  if (penalty === 0n) {
    return target <= cap ? target : null
  }

  let D = MathLib.mulDivUp(target, MathLib.WAD, MathLib.WAD + penalty)
  if (D > cap) {
    // The naive bound exceeds capacity — fall back to the cap and verify it covers.
    return adapterCoverage(cap, penalty) >= target ? cap : null
  }

  while (D > 0n && adapterCoverage(D - 1n, penalty) >= target) {
    D -= 1n
  }
  return D
}

// ─── computeMaxForceDeallocate ───────────────────────────────────────────────

const isMarketV1Adapter = (
  adapter: IAccrualVaultV2Adapter
): adapter is AccrualVaultV2MorphoMarketV1Adapter => 'positions' in adapter

export const isMarketV1AdapterV2 = (
  adapter: IAccrualVaultV2Adapter
): adapter is AccrualVaultV2MorphoMarketV1AdapterV2 => 'markets' in adapter

const isMorphoVaultV1Adapter = (
  adapter: IAccrualVaultV2Adapter
): adapter is AccrualVaultV2MorphoVaultV1Adapter => 'accrualVaultV1' in adapter

/**
 * Computes the per-(adapter, market) force-deallocate capacity, capped by shared market liquidity.
 *
 * - Skips MorphoVaultV1 adapters and any adapter with undefined penalty.
 * - Excludes the liquidity adapter's designated market (already covered by the normal withdraw).
 * - Processes adapters by ascending penalty so zero-penalty adapters claim shared market
 *   liquidity first, minimising fees downstream.
 */
export function computeMaxForceDeallocate(vaultV2: AccrualVaultV2): MaxForceDeallocateResult {
  const availableLiquidity = new Map<MarketId, bigint>()

  for (const adapter of vaultV2.accrualAdapters) {
    if (isMarketV1Adapter(adapter)) {
      for (const position of adapter.positions) {
        if (!availableLiquidity.has(position.marketId)) {
          availableLiquidity.set(position.marketId, position.market.liquidity)
        }
      }
    } else if (isMarketV1AdapterV2(adapter)) {
      for (const market of adapter.markets) {
        if (!availableLiquidity.has(market.id)) {
          availableLiquidity.set(market.id, market.liquidity)
        }
      }
    }
  }

  const liquidityAdapter = vaultV2.accrualLiquidityAdapter
  const liquidityAdapterAddress = liquidityAdapter?.address
  const liqMarketId =
    liquidityAdapter && vaultV2.liquidityData && vaultV2.liquidityData !== '0x'
      ? MarketParams.fromHex(vaultV2.liquidityData).id
      : undefined

  if (liquidityAdapter && liqMarketId) {
    if (isMarketV1Adapter(liquidityAdapter)) {
      for (const position of liquidityAdapter.positions) {
        if (position.marketId === liqMarketId) {
          const available = availableLiquidity.get(liqMarketId) ?? 0n
          availableLiquidity.set(
            liqMarketId,
            available - MathLib.min(position.supplyAssets, available)
          )
        }
      }
    } else if (isMarketV1AdapterV2(liquidityAdapter)) {
      for (const market of liquidityAdapter.markets) {
        if (market.id === liqMarketId) {
          const available = availableLiquidity.get(liqMarketId) ?? 0n
          const supplyAssets = market.toSupplyAssets(liquidityAdapter.supplyShares[market.id] ?? 0n)
          availableLiquidity.set(liqMarketId, available - MathLib.min(available, supplyAssets))
        }
      }
    }
  }

  const sortedAdapters = vaultV2.accrualAdapters.toSorted((a, b) => {
    const pa = vaultV2.forceDeallocatePenalties[a.address]
    const pb = vaultV2.forceDeallocatePenalties[b.address]
    if (pa === undefined) return 1
    if (pb === undefined) return -1
    return pa < pb ? -1 : pa > pb ? 1 : 0
  })

  const actions: ForceDeallocateAction[] = []
  let totalDeallocatable = 0n
  let totalCoverage = 0n

  for (const adapter of sortedAdapters) {
    if (isMorphoVaultV1Adapter(adapter)) continue
    const penalty = vaultV2.forceDeallocatePenalties[adapter.address]
    if (penalty === undefined) continue

    if (isMarketV1Adapter(adapter)) {
      for (const position of adapter.positions) {
        if (adapter.address === liquidityAdapterAddress && liqMarketId === position.marketId)
          continue
        const available = availableLiquidity.get(position.marketId) ?? 0n
        const amount = MathLib.min(position.supplyAssets, available)
        if (amount > 0n) {
          actions.push({
            adapter: adapter.address,
            amount,
            marketParams: position.market.params,
            penalty
          })
          totalDeallocatable += amount
          totalCoverage += adapterCoverage(amount, penalty)
          availableLiquidity.set(position.marketId, available - amount)
        }
      }
    } else if (isMarketV1AdapterV2(adapter)) {
      for (const market of adapter.markets) {
        if (adapter.address === liquidityAdapterAddress && liqMarketId === market.id) continue
        const supplyAssets = market.toSupplyAssets(adapter.supplyShares[market.id] ?? 0n)
        const available = availableLiquidity.get(market.id) ?? 0n
        const amount = MathLib.min(supplyAssets, available)
        if (amount > 0n) {
          actions.push({
            adapter: adapter.address,
            amount,
            marketParams: market.params,
            penalty
          })
          totalDeallocatable += amount
          totalCoverage += adapterCoverage(amount, penalty)
          availableLiquidity.set(market.id, available - amount)
        }
      }
    }
  }

  return { totalDeallocatable, totalCoverage, actions }
}

// ─── planForceWithdraw ───────────────────────────────────────────────────────

export interface PlannedDeallocation extends Deallocation {
  /** Penalty (assets) the adapter will charge for this deallocation amount. */
  penaltyAssets: bigint
}

export interface ForceWithdrawPlan {
  readonly deallocations: readonly PlannedDeallocation[]
  totalDeallocated: bigint
  totalPenaltyAssets: bigint
}

/**
 * Plans the deallocations for a `forceWithdraw` delivering `inputAmount` gross assets to the user.
 *
 * The plan satisfies the on-chain coverage equation:
 *
 *     shortfall  ≤  Σ (D_i + ceil(D_i × p_i / WAD))
 *
 * with `shortfall = inputAmount - availableNormalWithdraw`. That guarantees the post-deallocation
 * idle covers `withdraw.amount = inputAmount - totalPenaltyAssets`.
 *
 * There is no matching upper bound. The SDK's `vaultV2ForceWithdraw` / `vaultV2ForceRedeem`
 * encoders validate only a non-empty deallocation list and positive inputs — no `Σ D_i ≤
 * withdraw.amount` pre-check — and on-chain a surplus deallocation leaves the extra assets in vault
 * idle rather than removing them from the vault. Callers may therefore deliberately over-deallocate
 * to buffer share-price drift, at the cost of a larger penalty where the adapter charges one, since
 * the penalty is assessed on `D`.
 *
 * Allocation strategy: zero-penalty / lowest-penalty adapters first. The boundary adapter (the
 * one where the running coverage finally reaches the shortfall) is partially used at the minimal
 * `D` covering the remainder, so a penalty-bearing exit does not charge more fee than it needs.
 *
 * Returns `null` when:
 *  - `inputAmount` is non-positive;
 *  - no force-deallocation is needed (`availableNormalWithdraw` already covers `inputAmount`);
 *  - the shortfall cannot be covered by `actions` (insufficient totalCoverage).
 */
export function planForceWithdraw({
  inputAmount,
  availableNormalWithdraw,
  actions
}: {
  inputAmount: bigint
  availableNormalWithdraw: bigint
  actions: readonly ForceDeallocateAction[]
}): ForceWithdrawPlan | null {
  if (inputAmount <= 0n) return null
  if (inputAmount <= availableNormalWithdraw) return null

  const shortfall = inputAmount - availableNormalWithdraw

  // Lowest penalty first; ties broken by largest capacity (fewer adapters touched).
  const sorted = actions.toSorted((a, b) =>
    a.penalty !== b.penalty
      ? a.penalty < b.penalty
        ? -1
        : 1
      : a.amount > b.amount
        ? -1
        : a.amount < b.amount
          ? 1
          : 0
  )

  const deallocations: PlannedDeallocation[] = []
  let totalDeallocated = 0n
  let totalPenaltyAssets = 0n
  let coverage = 0n

  for (const action of sorted) {
    if (coverage >= shortfall) break
    if (action.penalty > MAX_FORCE_DEALLOCATE_PENALTY) {
      failInvariant('[planForceWithdraw] INVARIANT_penalty_exceeds_2pct_cap')
    }

    const remaining = shortfall - coverage
    const fullStep = adapterCoverage(action.amount, action.penalty)

    let D: bigint
    if (fullStep <= remaining) {
      // This adapter alone does not bridge the gap — commit its full capacity and continue.
      D = action.amount
    } else {
      // Boundary adapter: pick the smallest D covering `remaining`.
      const minD = minDeallocationFor(remaining, action.penalty, action.amount)
      if (minD === null) failInvariant('[planForceWithdraw] INVARIANT_minD_unreachable')
      D = minD
    }

    const penalty = penaltyAssets(D, action.penalty)
    deallocations.push({
      adapter: action.adapter,
      marketParams: action.marketParams,
      amount: D,
      penaltyAssets: penalty
    })
    totalDeallocated += D
    totalPenaltyAssets += penalty
    coverage += D + penalty
  }

  if (coverage < shortfall) return null

  return { deallocations, totalDeallocated, totalPenaltyAssets }
}

/**
 * `previewWithdraw` mirror — `ceil(assets × (totalSupply + virtualShares) / (totalAssets + 1))`.
 * Used to translate per-adapter penalty assets into the share burn the contract will perform: the
 * redeem path reserves those shares and redeems only the remainder, and the asset path caps
 * `withdraw.amount` at what the shares surviving them are worth.
 */
export function previewWithdrawShares(
  vault: { _totalAssets: bigint; totalSupply: bigint; virtualShares: bigint },
  assets: bigint
): bigint {
  if (assets <= 0n) return 0n
  return MathLib.mulDivUp(assets, vault.totalSupply + vault.virtualShares, vault._totalAssets + 1n)
}

/**
 * Largest in-kind exit the user's shares can actually pay for.
 *
 * The contract burns shares in three separate operations — the idle leg, the penalty leg and
 * the in-kind leg — and each `previewWithdraw` rounds up. Split burns therefore cost a few
 * shares more than one merged burn of the same assets. That only bites at MAX, where the exit
 * is the whole position and nothing is spare to absorb them, and it surfaces as a bare
 * `panic 0x32` from VaultExitBundlesV1 (which documents needing `exitAssets + N`). The SDK
 * does not defend it either: share sufficiency is an explicit non-goal in the sdks#909 decision record.
 *
 * Mirrors `forceWithdrawAmount`: reserve the penalty's shares, then cap the two withdraw legs
 * at whatever the remainder converts to. `toAssets` floors and `previewWithdrawShares` ceils.
 *
 * On top of the penalty we reserve a slice of the user's own shares. The exit is quoted against
 * a block-pinned snapshot but executes later, and if the share price has fallen in between, the
 * assets the snapshot promised cost more shares than the user holds — the burn underflows and
 * the bundle reverts with `panic 0x11`. The deficit is `|relative price drop| × userShares`, so
 * it scales with the position: a fixed share count cannot cover it (the old 2-share constant
 * rounded away to nothing at any real size). Share price falls whenever the management fee
 * outruns the vault's gross yield — ordinary, not exotic.
 *
 * Sizing: the reserve must outlive the bundle, not just the signing pause — the SDK's IKR
 * bundle stays valid for two hours, and a Safe proposal (or an EOA prompt left open) can
 * execute at the end of that window. Worst-case decay is the contract's 5%/yr management-fee
 * cap, so 2e-5 (0.2 bp) buys ~3.5 hours of snapshot staleness at that cap — the full validity
 * window with margin — and days at a typical 0.25% fee, at a cost of ~2 USDC stranded on a
 * 100k position. The previous 1e-6 covered only ~10.5 minutes at the cap, shorter than the
 * bundle it was guarding. The floor keeps the two shares of split-rounding headroom that tiny
 * positions still need.
 *
 * What this does NOT defend: a discrete NAV loss (bad-debt realization, an oracle repricing),
 * which is unbounded and can dwarf any proportional reserve. That needs a fresh snapshot, not
 * a bigger buffer.
 */
const DRIFT_RESERVE_FLOOR_SHARES = 2n

/**
 * One part in this many of the user's shares — the 2e-5 coefficient the sizing note above prices
 * out. Deliberately far tighter than `FORCE_REDEEM_BUFFER_BPS`; see the note there for why the
 * two cannot be reconciled to one number. Tighter again than `CAPACITY_MARGIN_DIVISOR` below,
 * because this reserve is stranded as dust in the user's position while that one merely stays
 * deposited in the Vault.
 */
export const DRIFT_RESERVE_DIVISOR = 50_000n

/**
 * One part in this many of the Market's capacity — the 1 bp margin `capExitToMarketCapacity`
 * withholds. Third of the three in-kind reserves, alongside `FORCE_REDEEM_BUFFER_BPS` and
 * `DRIFT_RESERVE_DIVISOR`: each answers a different hazard, and none of them bounds the others.
 */
export const CAPACITY_MARGIN_DIVISOR = 10_000n // 1 bp

/**
 * Holds a capacity-bound exit just under the Market's ceiling.
 *
 * `maxExitAssets` comes from a block-pinned snapshot, but the deallocation runs against whatever
 * the Market can serve a few blocks later. An exit quoted at exactly the ceiling therefore asks
 * for more than the bundler's deallocate loop can find, and the loop runs off the end of its
 * market list: the user pays the gas for a bare `panic 0x32` and is told nothing. Withholding a
 * sliver keeps the request strictly inside what the Market will still hold at execution.
 *
 * Sizing: the overshoot observed on 18 August was ~8e-6 of the exit, so 1 bp covers it about
 * twelve times over. Unlike `DRIFT_RESERVE_DIVISOR`, this margin strands nothing — the withheld
 * assets stay deposited in the Vault, the partial-redemption callout names them, and the user
 * takes them on the next round. That is why it can afford to be the loosest of the three.
 *
 * What this does NOT cover: `maxExitAssets` is derived from the Vault's allocation to the Market
 * and models no Market liquidity at all. A liquidity crunch, where Blue's loan-token balance
 * binds before the allocation does, is a different failure that no margin on the allocation can
 * answer.
 *
 * The margin is a ceiling every request is held under, not a shave applied at the boundary
 * point. Drift is a band, not an equality: a request one wei below `maxExitAssets` overshoots
 * the same allocation the exact-cap request does, so exempting it would leave the whole band
 * the margin exists to absorb unprotected. Anything below the margin-adjusted ceiling is
 * limited by the user rather than by the Market and passes through untouched.
 */
export function capExitToMarketCapacity({
  exitAssets,
  maxExitAssets
}: {
  exitAssets: bigint
  maxExitAssets: bigint
}): bigint {
  const cappedMaxExitAssets = MathLib.zeroFloorSub(
    maxExitAssets,
    MathLib.mulDivUp(maxExitAssets, 1n, CAPACITY_MARGIN_DIVISOR)
  )
  return MathLib.min(exitAssets, cappedMaxExitAssets)
}

export function capInKindExitToShares({
  exitAssets,
  feeAssets,
  userVaultShares,
  vault
}: {
  exitAssets: bigint
  feeAssets: bigint
  userVaultShares: bigint
  vault: {
    _totalAssets: bigint
    totalSupply: bigint
    virtualShares: bigint
    toAssets: (shares: bigint) => bigint
  }
}): bigint {
  const reservedShares =
    previewWithdrawShares(vault, feeAssets) +
    MathLib.max(
      DRIFT_RESERVE_FLOOR_SHARES,
      MathLib.mulDivUp(userVaultShares, 1n, DRIFT_RESERVE_DIVISOR)
    )
  // Nothing left for the withdraw legs. The SDK validates the chain, the adapter and the
  // market's coverage but never checks the burn against the user's balance, so returning the
  // request unchanged would build a bundle that reverts on chain and spends the gas. Zero makes
  // the builder throw `NonPositiveInputError`, which the review dialog already surfaces.
  if (reservedShares >= userVaultShares) return 0n
  // The share reserve alone is not enough when shares are finer-grained than the asset: worth
  // less than one asset atom, it rounds away in `toAssets` and a MAX exit comes back unchanged —
  // which the SDK then rejects, since it requires one asset unit of headroom per market
  // (`amount + marketParamsList.length <= previewRedeem(sharesHeld)`). Every in-kind flow here
  // redeems into exactly one market, so one atom of the asset is withheld too.
  const withdrawableAssets = MathLib.zeroFloorSub(
    vault.toAssets(userVaultShares - reservedShares),
    1n
  )
  return MathLib.min(exitAssets, withdrawableAssets + feeAssets)
}

/**
 * `capInKindExitToShares` with the fee priced against the exit actually taken, not the exit
 * requested. Fee and cap depend on each other: the fee of a request far above the live position
 * can reserve more shares than the whole balance holds and zero an exit that a fee priced on
 * the smaller, capped amount would happily fund (request 100k against a 1k position at a 2%
 * penalty: the 1.9k fee reservation swallows the balance, while the real exit's fee is ~19).
 * So iterate to the fixed point — each round reprices the fee against the previous round's
 * exit; in practice it settles in two rounds, and four bound the rounding oscillation. The
 * final pass re-caps with the settled fee so the result never exceeds what that fee's
 * reservation leaves fundable — the safe side, since the bundle is rebuilt and re-simulated
 * from whatever this returns.
 */
export function capInKindExitToSharesWithFee({
  exitAssets,
  userVaultShares,
  vault,
  feeOf
}: {
  exitAssets: bigint
  userVaultShares: bigint
  vault: Parameters<typeof capInKindExitToShares>[0]['vault']
  /** Prices the redemption fee of a given exit — see `inKindRedemptionFeeAssets`. */
  feeOf: (exitAssets: bigint) => bigint
}): bigint {
  let capped = exitAssets
  for (let round = 0; round < 4; round++) {
    const next = capInKindExitToShares({
      exitAssets,
      feeAssets: feeOf(capped),
      userVaultShares,
      vault
    })
    if (next === capped) return capped
    capped = next
  }
  return MathLib.min(
    capped,
    capInKindExitToShares({ exitAssets, feeAssets: feeOf(capped), userVaultShares, vault })
  )
}
